import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { isRole, isRoleType } from "../../redux/selectors/authSelector";
import Loader from "../../components/common/Loader";
import { useCountryNavigate } from "../../components/common/UseCountryNavigate";
import { toast } from "sonner";
import axios from "axios";

// Recipient module configuration
const RECIPIENT_MODULES = [
  { value: "parent", label: "Parents", singularLabel: "Parent" },
  { value: "school_management", label: "School Management Members", singularLabel: "Member" },
  { value: "board", label: "Board Members", singularLabel: "Member" },
  { value: "teacher", label: "Teachers", singularLabel: "Teacher" },
];

// Map recipient module to its list API endpoint & response data key
const getRecipientListConfig = (recipientModule) => {
  switch (recipientModule) {
    case "parent":
      return {
        url: `${process.env.REACT_APP_API_URL}admin/parent-list`,
        dataKey: "parents",
        nameField: "name",
        mobileField: "mobile_number",
      };
    case "school_management":
      return {
        endpoint: HttpService.school_management_list,
        dataKey: "managers",
        nameField: "name",
        mobileField: "mobile",
      };
    case "board":
      return {
        endpoint: HttpService.board_management_list,
        dataKey: "boards",
        nameField: "name",
        mobileField: "mobile",
      };
    case "teacher":
      return {
        endpoint: HttpService.teacher_management_list,
        dataKey: "managers",
        nameField: "name",
        mobileField: "mobile",
      };
    default:
      return null;
  }
};

const AddCommunication = () => {
  const [formData, setFormData] = useState({
    message: "",
    type: "instant",
    scheduled_at: "",
    recipient_module: "parent",
    target: "all",
    recipient_ids: [],
  });

  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [recipientList, setRecipientList] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const initialEditLoaded = useRef(false);

  const params = useParams();
  const location = useLocation();
  const navigate = useCountryNavigate();
  const availableCredit = location.state?.credit ?? null;
  const role = useSelector(isRole);
  const role_type = useSelector(isRoleType);
  const schoolId = localStorage.getItem("activeSchoolId");
  const mode = localStorage.getItem("mode");

  // Get current module config
  const currentModule = RECIPIENT_MODULES.find(
    (m) => m.value === formData.recipient_module
  ) || RECIPIENT_MODULES[0];

  // Validate form
  const validateForm = () => {
    let newErrors = {};

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    if (formData.type === "scheduled" && !formData.scheduled_at) {
      newErrors.scheduled_at =
        "Schedule date & time is required for scheduled messages";
    }

    if (
      formData.target === "selected" &&
      formData.recipient_ids.length === 0
    ) {
      newErrors.recipient_ids = `At least one ${currentModule.singularLabel.toLowerCase()} must be selected`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // When recipient_module changes, reset recipient selection
    if (name === "recipient_module") {
      setFormData((prev) => ({
        ...prev,
        recipient_module: value,
        recipient_ids: [],
        target: "all",
      }));
      setRecipientList([]);
      setRecipientSearch("");
      return;
    }

    // When target changes from selected to all, clear selection
    if (name === "target" && value === "all") {
      setFormData((prev) => ({ ...prev, target: value, recipient_ids: [] }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Toggle recipient selection
  const toggleRecipient = (recipientId) => {
    setFormData((prev) => {
      const ids = prev.recipient_ids.includes(recipientId)
        ? prev.recipient_ids.filter((id) => id !== recipientId)
        : [...prev.recipient_ids, recipientId];
      return { ...prev, recipient_ids: ids };
    });

    // Clear error if selecting
    if (errors.recipient_ids) {
      setErrors((prev) => ({ ...prev, recipient_ids: "" }));
    }
  };

  // Select/Deselect all recipients
  const toggleAllRecipients = () => {
    if (formData.recipient_ids.length === recipientList.length) {
      setFormData((prev) => ({ ...prev, recipient_ids: [] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        recipient_ids: recipientList.map((r) => r._id),
      }));
    }
  };

  // Fetch recipient list based on module
  const fetchRecipientsByModule = async (moduleValue, search = "") => {
    const config = getRecipientListConfig(moduleValue);
    if (!config) return;

    setLoadingRecipients(true);
    try {
      let items = [];

      if (config.url) {
        // Parent uses direct axios call
        const res = await axios.post(config.url, {
          page: 1,
          perpage: 1000,
          search: search,
          school_id: schoolId,
        });
        if (res.data.status === 200) {
          items = res.data.data[config.dataKey] || [];
        }
      } else if (config.endpoint) {
        // Others use Postdata with HttpService endpoints
        const res = await Postdata(config.endpoint, {
          page: 1,
          perpage: 1000,
          search: search,
          school_id: schoolId,
        });
        if (res.status === 200) {
          // Handle various response shapes
          const resData = res.data;
          if (Array.isArray(resData)) {
            items = resData;
          } else if (resData && Array.isArray(resData[config.dataKey])) {
            items = resData[config.dataKey];
          } else if (resData && Array.isArray(resData.data)) {
            items = resData.data;
          } else {
            items = [];
          }
        }
      }

      setRecipientList(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error("Error fetching recipients", error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoader(true);
    try {
      const payload = {
        message: formData.message,
        type: formData.type,
        target: formData.target,
        recipient_module: formData.recipient_module,
        school_id: schoolId,
      };

      if (formData.type === "scheduled") {
        payload.scheduled_at = formData.scheduled_at;
      }

      if (formData.target === "selected") {
        payload.recipient_ids = formData.recipient_ids;
      }

      let res;
      if (params.id) {
        payload.id = params.id;
        res = await Postdata(HttpService.communication_update, payload);
      } else {
        res = await Postdata(HttpService.communication_add, payload);
      }

      if (res.status === 200) {
        toast.success(res.message || "Communication saved successfully");
        handleBackNavigation();
      } else {
        toast.error(res.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      const errMsg =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Failed to save communication";
      toast.error(errMsg);
    } finally {
      setLoader(false);
    }
  };

  // In edit mode: fetch communication data and its recipients
  useEffect(() => {
    if (!params.id) return;

    const loadEditData = async () => {
      setLoader(true);
      try {
        // Fetch communication data
        const res = await Postdata(HttpService.communication_show, {
          id: params.id,
        });

        if (res.status === 200) {
          const comm = res.data;
          const module = comm.recipient_module || "parent";

          // Fetch recipient list for this module
          await fetchRecipientsByModule(module);

          setFormData({
            message: comm.message || "",
            type: comm.type || "instant",
            scheduled_at: comm.scheduled_at
              ? new Date(comm.scheduled_at).toISOString().slice(0, 16)
              : "",
            recipient_module: module,
            target: comm.target || "all",
            recipient_ids: comm.selected_recipients
              ? comm.selected_recipients.map((r) => r._id || r)
              : [],
          });
        } else {
          toast.error("Communication not found");
        }
      } catch (err) {
        toast.error("Failed to fetch communication");
      } finally {
        setLoader(false);
      initialEditLoaded.current = true;
      }
    };

    loadEditData();
  }, [params.id]);

  // Fetch recipients when target = selected (for new communication or search changes)
  useEffect(() => {
    if (formData.target !== "selected") return;
    // Skip only the very first load in edit mode (already handled by loadEditData)
    if (params.id && !initialEditLoaded.current) return;

    const timer = setTimeout(() => {
      fetchRecipientsByModule(formData.recipient_module, recipientSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.target, formData.recipient_module, recipientSearch, schoolId]);

  // Back navigation
  const handleBackNavigation = () => {
    if (
      role === "admin" ||
      role === "subadmin" ||
      role_type === "schoolsubadmin" ||
      role_type === "subadmin"
    ) {
      if (mode === "view") {
        navigate(`admin-school/view/${schoolId}`, {
          state: { mainTab: "Communication" },
        });
      } else {
        navigate("navigation-dashboard", {
          state: { mainTab: "Communication" },
        });
      }
    } else {
      navigate("communication");
    }
  };

  // Get mobile field based on module
  const getMobileDisplay = (recipient) => {
    const config = getRecipientListConfig(formData.recipient_module);
    if (!config) return "";
    return recipient[config.mobileField] || "";
  };

  if (loader && params.id) {
    return <Loader isClass="loader_fullscreen" />;
  }

  return (
    <div className="p-6 space-y-6 pr-0">
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full bg-white rounded-xl p-6">
          {/* Header */}
          <div
            className="flex items-center mb-4 cursor-pointer"
            onClick={handleBackNavigation}
          >
            <FiArrowLeft className="text-2xl font-bold mb-6 text-gray-800" />
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              {params.id ? "Edit Communication" : "New Communication"}
            </h2>
            {availableCredit !== null && (
              <div className="ml-auto flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-6">
                <span className="text-sm font-medium text-blue-700">Available Credits:</span>
                <span className={`text-lg font-bold ${availableCredit <= 10 ? 'text-red-600' : 'text-blue-800'}`}>{availableCredit}</span>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            noValidate
            className="space-y-5"
          >
            {/* Recipient Module */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Recipient Type <span className="text-red-500">*</span>
              </label>
              <select
                name="recipient_module"
                value={formData.recipient_module}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
              >
                {RECIPIENT_MODULES.map((mod) => (
                  <option key={mod.value} value={mod.value}>
                    {mod.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={200}
                className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your SMS message here..."
              />
              <div className="flex justify-between items-center">
                {errors.message && (
                  <p className="text-red-500 text-sm">{errors.message}</p>
                )}
                <span className={`text-xs ml-auto ${200 - formData.message.length <= 10 ? 'text-red-500' : 'text-gray-500'}`}>
                  {200 - formData.message.length} characters remaining
                </span>
              </div>
              
                <p className="text-orange-600 text-xs mt-1">
                  ⚠️ Note: <br/>Messages up to 100 characters will consume 1 credit.<br/>
                      Messages exceeding 100 characters will consume 2 credits.
                </p>
              
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="instant">Instant</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>

            {/* Schedule Date & Time */}
            {formData.type === "scheduled" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Schedule Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="scheduled_at"
                  value={formData.scheduled_at}
                  onChange={handleChange}
                  min={new Date().toISOString().slice(0, 16)}
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
                />
                {errors.scheduled_at && (
                  <p className="text-red-500 text-sm">{errors.scheduled_at}</p>
                )}
              </div>
            )}

            {/* Target */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Target <span className="text-red-500">*</span>
              </label>
              <select
                name="target"
                value={formData.target}
                onChange={handleChange}
                className="mt-1 w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All {currentModule.label}</option>
                <option value="selected">Select {currentModule.label}</option>
              </select>
            </div>

            {/* Recipient Selection */}
            {formData.target === "selected" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select {currentModule.label}{" "}
                  <span className="text-red-500">*</span>
                </label>

                {/* Search */}
                <input
                  type="text"
                  placeholder={`Search ${currentModule.label.toLowerCase()}...`}
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 mb-2 focus:ring-2 focus:ring-blue-500"
                />

                {/* Selected count & select all */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">
                    {formData.recipient_ids.length}{" "}
                    {currentModule.singularLabel.toLowerCase()}(s) selected
                  </span>
                  <button
                    type="button"
                    onClick={toggleAllRecipients}
                    className="text-sm text-primary_main hover:underline"
                  >
                    {formData.recipient_ids.length === recipientList.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>

                {/* Recipient list */}
                <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                  {loadingRecipients ? (
                    <div className="p-4 text-center text-gray-400">
                      Loading {currentModule.label.toLowerCase()}...
                    </div>
                  ) : recipientList.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">
                      No {currentModule.label.toLowerCase()} found
                    </div>
                  ) : (
                    recipientList.map((recipient) => (
                      <label
                        key={recipient._id}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={formData.recipient_ids.includes(
                            recipient._id
                          )}
                          onChange={() => toggleRecipient(recipient._id)}
                          className="mr-3 h-4 w-4 text-primary_main border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <span className="font-medium text-sm">
                            {recipient.name}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {getMobileDisplay(recipient)}
                          </span>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {errors.recipient_ids && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.recipient_ids}
                  </p>
                )}
              </div>
            )}

            {/* Submit */}
            <div className="w-full flex justify-center gap-3 mb-2 mt-4">
              <button
                type="submit"
                className="button-field-primary"
                style={{ width: "30%" }}
                disabled={loader}
              >
                {loader
                  ? "Please wait..."
                  : params.id
                  ? "Update"
                  : formData.type === "instant"
                  ? "Send Now"
                  : "Schedule"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCommunication;
